/**
 * Derives the whole design-system palette from one brand colour.
 *
 * The hand-tuned defaults in src/index.css pair a eucalyptus accent with warm
 * cream paper. That pairing was chosen for *that* green — swap in a blue or a
 * plum and the cream reads as a mismatch rather than a companion. So a custom
 * primary repaints the surfaces too: same lightness as the shipped palette
 * (contrast is already proven there), the brand's own hue, and a whisper of
 * its chroma.
 *
 * Everything happens in OKLCH, where lightness is perceptual: holding L fixed
 * and moving only hue and chroma keeps every text/background pair as legible
 * as the palette it was measured from. The default primary derives nothing at
 * all — see paletteCss — so Astute's shipped combination survives untouched.
 */

/** The one shape a stored or typed primary colour must match. */
export const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/

export type Oklch = { L: number; C: number; h: number }

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

const srgbToLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
const linearToSrgb = (c: number) => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055)

/** sRGB hex -> OKLCH. Returns null for anything that isn't a #rrggbb string. */
export function hexToOklch(hex: string): Oklch | null {
  if (!HEX_COLOUR.test(hex)) return null
  const n = parseInt(hex.slice(1), 16)
  const r = srgbToLinear(((n >> 16) & 255) / 255)
  const g = srgbToLinear(((n >> 8) & 255) / 255)
  const b = srgbToLinear((n & 255) / 255)

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

  const h = (Math.atan2(bb, a) * 180) / Math.PI
  return { L, C: Math.hypot(a, bb), h: h < 0 ? h + 360 : h }
}

/** OKLCH -> linear sRGB, unclamped: channels outside 0..1 mean out of gamut. */
function toLinearRgb({ L, C, h }: Oklch): [number, number, number] {
  const rad = (h * Math.PI) / 180
  const a = C * Math.cos(rad)
  const b = C * Math.sin(rad)

  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3

  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
}

const IN_GAMUT = (rgb: number[]) => rgb.every((c) => c >= -0.0001 && c <= 1.0001)

/**
 * OKLCH -> sRGB hex. A brand hue at the chroma we asked for may not exist in
 * sRGB (saturated yellows and cyans run out of room fast), so an out-of-gamut
 * colour loses chroma until it fits rather than losing lightness — desaturating
 * preserves the contrast the token was picked for; clipping channels would not.
 */
export function oklchToHex(colour: Oklch): string {
  const L = clamp(colour.L, 0, 1)
  let C = Math.max(colour.C, 0)

  if (!IN_GAMUT(toLinearRgb({ ...colour, L, C }))) {
    let lo = 0
    let hi = C
    // 20 halvings resolve chroma far finer than an 8-bit channel can show.
    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2
      if (IN_GAMUT(toLinearRgb({ L, C: mid, h: colour.h }))) lo = mid
      else hi = mid
    }
    C = lo
  }

  const hex = toLinearRgb({ L, C, h: colour.h })
    .map((c) => Math.round(clamp(linearToSrgb(c), 0, 1) * 255).toString(16).padStart(2, '0'))
    .join('')
  return `#${hex}`
}

/** WCAG 2.x relative luminance, for choosing legible text over a fill. */
function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16)
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => srgbToLinear(c / 255))
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

const contrast = (a: string, b: string) => {
  const [x, y] = [luminance(a), luminance(b)]
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

/**
 * Lightness and chroma of every neutral token in src/index.css, measured from
 * the shipped palette. A derived palette reuses these lightnesses verbatim and
 * only re-hues them, so a custom brand can shift the mood without moving any
 * text/background pair off the contrast it ships with today.
 */
const NEUTRALS = {
  light: {
    paper: [0.9645, 0.0098],
    surface: [0.9942, 0.0069],
    'surface-2': [0.9465, 0.014],
    ink: [0.2829, 0.0107],
    muted: [0.547, 0.0196],
    line: [0.899, 0.0215],
  },
  dark: {
    paper: [0.2048, 0.0061],
    surface: [0.2431, 0.0082],
    'surface-2': [0.2718, 0.0102],
    ink: [0.9392, 0.0081],
    muted: [0.6736, 0.0152],
    line: [0.3275, 0.012],
  },
} as const

/** Chroma of the shipped eucalyptus accent — the reference a brand scales against. */
const REFERENCE_CHROMA = 0.062

export type PaletteTokens = Record<string, string>

/**
 * Both themes' tokens for a brand colour, or null if the colour is unusable
 * (a half-typed hex, most often — the settings field stores every keystroke).
 */
export function derivePalette(primary: string): { light: PaletteTokens; dark: PaletteTokens } | null {
  const base = hexToOklch(primary)
  if (!base) return null

  // A muted brand gets muted surfaces and a vivid one gets slightly livelier
  // surfaces, but never more than a hint: past ~1.25x the reference the tint
  // stops reading as a neutral and starts reading as a colour.
  const tint = clamp(base.C / REFERENCE_CHROMA, 0, 1.25)

  const build = (mode: 'light' | 'dark'): PaletteTokens => {
    const neutrals = NEUTRALS[mode]
    const tokens: PaletteTokens = {}
    for (const [name, [L, C]] of Object.entries(neutrals)) {
      tokens[`--${name}`] = oklchToHex({ L, C: C * tint, h: base.h })
    }

    // Light keeps the brand colour exactly as entered — it is the brand. Dark
    // has to lift a deep brand clear of a near-black background, the same move
    // the shipped palette makes by hand (eucalyptus L0.50 -> L0.75). A brand
    // that is already light enough is left where it is: lifting it further only
    // washes it out.
    const accent =
      mode === 'light'
        ? { ...base }
        : {
            L: base.L >= 0.62 ? Math.min(base.L, 0.88) : clamp(base.L + 0.25, 0.62, 0.78),
            C: base.C * 1.3,
            h: base.h,
          }

    tokens['--accent'] = oklchToHex(accent)
    // Hover: a proportional darkening, so a pale brand darkens gently and a
    // deep one still has somewhere to go.
    tokens['--accent-deep'] = oklchToHex({
      ...accent,
      L: mode === 'light' ? Math.max(0.12, accent.L * 0.84) : accent.L - 0.045,
    })
    // Chip and focus-ring wash: the accent hue at the shipped tint lightness.
    tokens['--accent-tint'] = oklchToHex({
      L: mode === 'light' ? 0.944 : 0.295,
      C: Math.min(mode === 'light' ? 0.03 : 0.04, base.C * (mode === 'light' ? 0.25 : 0.33)),
      h: base.h,
    })
    // Accent as text, on paper or on the tint — a different job from accent as
    // fill, and the one a pale brand breaks: a sky blue reads fine as a button
    // and disappears as an eyebrow. Clamped from the direction that theme needs
    // (dark enough on light paper, light enough on dark) rather than taken from
    // the brand as-is.
    const textL =
      mode === 'light' ? Math.min(base.L, 0.52) : Math.max(accent.L, 0.7)
    tokens['--accent-text'] = oklchToHex({ ...accent, L: textL })
    // The emphasis step moves away from the page rather than in a fixed
    // direction: darker on light paper, brighter on dark. Going darker in dark
    // mode is what the shipped palette does by hand, but it has nowhere to go
    // once the text is already floored for legibility — a vivid brand's tint
    // pulled it under 4.5:1 against its own chip background.
    tokens['--accent-text-strong'] = oklchToHex({
      ...accent,
      L: mode === 'light' ? Math.min(textL * 0.84, 0.42) : Math.min(textL + 0.07, 0.92),
    })

    // --apricot and --danger are deliberately absent: both are semantic, not
    // decorative. Apricot is the amber a Draft chip and the draft banner are
    // read by, and a brand-rotated "draft" marker that came out green would
    // read as approved on regulatory content. They stay as index.css sets them.
    //
    // Label on a filled accent button. White suits most brands and is what the
    // shipped palette uses, but a pale or yellow brand needs dark text — pick
    // whichever actually contrasts rather than assuming.
    const onLight = oklchToHex({ L: 0.22, C: base.C * 0.2, h: base.h })
    tokens['--on-accent'] =
      contrast(tokens['--accent'], '#ffffff') >= contrast(tokens['--accent'], onLight)
        ? '#ffffff'
        : onLight

    return tokens
  }

  return { light: build('light'), dark: build('dark') }
}

const declarations = (tokens: PaletteTokens) =>
  Object.entries(tokens)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n')

/**
 * The derived palette as a stylesheet. Three outcomes, and the caller has to
 * treat them differently:
 *
 * - a stylesheet — apply it;
 * - `''` at the design-system default — override nothing, because the shipped
 *   cream-and-eucalyptus pairing is hand-tuned and beats any derivation of it;
 * - `null` for a colour that can't be read — hold whatever is already applied.
 *   The settings field stores every keystroke, so '#12' arrives on the way to
 *   '#123456'; repainting from it would flicker the page back to the shipped
 *   palette and out again on every character typed.
 *
 * Selectors mirror src/index.css's three theme cases (explicit light, explicit
 * dark, and `system` following the OS) and double `:root` so this wins on
 * specificity wherever it lands in the document rather than on source order.
 */
export function paletteCss(primary: string, defaultPrimary: string): string | null {
  if (primary === defaultPrimary) return ''
  const palette = derivePalette(primary)
  if (!palette) return null
  const dark = declarations(palette.dark)
  return [
    `:root:root {\n${declarations(palette.light)}\n}`,
    `:root:root[data-theme='dark'] {\n${dark}\n}`,
    `@media (prefers-color-scheme: dark) {\n  :root:root:not([data-theme]) {\n${dark}\n  }\n}`,
  ].join('\n')
}
